import { Launch } from "@mui/icons-material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import { Search, Clear } from "@mui/icons-material";
import {
  Box,
  Button,
  TextField,
  IconButton,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  CompositeFilterDescriptor,
  DataResult,
  process,
} from "@progress/kendo-data-query";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { GridColumnProps } from "@progress/kendo-react-grid";
import { DataGridWithSort } from "../../components/DataTable/DataGridWithSort";
import { FormattedNumberCell } from "../../components/DataTable/customcell/FormattedNumberCell";
import {
  fetchInventorySummary,
  selectInventorySummary,
} from "../../slices/inventorymanagement.slice";
import { InventoryDetailModal } from "./InventoryDetailModal";
import { EditInventoryModal } from "./EditInventoryModal";

const initialFilters: CompositeFilterDescriptor = {
  logic: "and",
  filters: [],
};

// Helper function to format numbers to 2 decimal places
const formatNumber = (value: any): string => {
  if (value === null || value === undefined || value === '') return '0.00';
  const num = parseFloat(value);
  return isNaN(num) ? '0.00' : num.toFixed(2);
};

export const InventoryManagement = () => {
  const dispatch = useAppDispatch();
  const [filter, setFilter] = useState<CompositeFilterDescriptor>(initialFilters);
  const exportRef = useRef<ExcelExport | null>(null);

  const inventorySummary = useAppSelector(selectInventorySummary);
  
  // Local loading state
  const [isLoading, setIsLoading] = useState<boolean>(true);
 
  const [productSearchQuery, setProductSearchQuery] = useState<string>("");
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);

  const onViewClick = (dataItem: any) => {
    setSelectedItem(dataItem);
    setDetailModalOpen(true);
  };

  const onEditClick = (dataItem: any) => {
    setSelectedItem(dataItem);
    setEditModalOpen(true);
  };

  // Transform data to include formatted numbers
  const transformedData = inventorySummary.map(item => ({
    ...item,
    currentStockFormatted: formatNumber(item.currentStock),
    totalPurchasedFormatted: formatNumber(item.totalPurchased),
    totalAppliedFormatted: formatNumber(item.totalApplied),
  }));

  const gridColumns: GridColumnProps[] = [
    {
      field: "productName",
      title: "Product Name",
      width: "180px",
    },
    {
      field: "unit",
      title: "Unit",
      width: "120px",
    },
    {
      field: "openingStock",
      title: "Opening Stock",
      width: "120px",
    },
    {
      field: "totalPurchased",
      title: "Total Purchased",
      width: "150px",
      cells: {
        data: (props: any) => <FormattedNumberCell {...props} field="totalPurchased" />,
      },
    },
    {
      field: "totalApplied",
      title: "Total Applied",
      width: "150px",
      cells: {
        data: (props: any) => <FormattedNumberCell {...props} field="totalApplied" />,
      },
    },
    {
      field: "currentStock",
      title: "Closing Stock",
      width: "150px",
      cells: {
        data: (props: any) => <FormattedNumberCell {...props} field="currentStock" />,
      },
    },
    {
      field: "lastPurchaseDate",
      title: "Last Purchase",
      width: "150px",
    },
    {
      field: "lastApplicationDate",
      title: "Last Application",
      width: "150px",
    },
    {
      title: "Actions",
      width: "120px",
      cells: {
        data: (props: any) => (
          <td 
            className="k-table-td"
            style={{
              borderBottom: '1px solid var(--kendo-color-border)',
              borderLeft: 'none',
              borderRight: 'none',
              fontFamily: 'Roboto',
              textAlign: 'center',
              padding: '12px 8px',
            }}
          >
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              gap: '12px', 
              alignItems: 'center',
              height: '24px'
            }}>
              <VisibilityIcon 
                fontSize="small" 
                style={{ 
                  cursor: 'pointer',
                  color: '#6c757d',
                  transition: 'color 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#007bff'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#6c757d'}
                onClick={() => onViewClick(props.dataItem)}
                titleAccess="View Details"
              />
              <ModeEditIcon 
                fontSize="small" 
                style={{ 
                  cursor: 'pointer',
                  color: '#6c757d',
                  transition: 'color 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#007bff'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#6c757d'}
                onClick={() => onEditClick(props.dataItem)}
                titleAccess="Edit Inventory"
              />
            </div>
          </td>
        ),
      },
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        await dispatch(fetchInventorySummary()).unwrap();
      } catch (error) {
        console.error('Failed to fetch inventory summary:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [dispatch]);

  const handleProductSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setProductSearchQuery(e.target.value);
  };

  const handleProductSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      // Optional: trigger search on Enter key
      updateFilter();
    }
  };

  const handleClearSearch = () => {
    setProductSearchQuery("");
  };

  const updateFilter = () => {
    if (productSearchQuery.trim()) {
      const filterObj: CompositeFilterDescriptor = {
        logic: "or",
        filters: [
          {
            field: "productName",
            operator: "contains",
            value: productSearchQuery,
          },
          {
            field: "currentStockFormatted",
            operator: "contains",
            value: productSearchQuery,
          },
          {
            field: "totalPurchasedFormatted",
            operator: "contains",
            value: productSearchQuery,
          },
          {
            field: "totalAppliedFormatted",
            operator: "contains",
            value: productSearchQuery,
          },
          {
            field: "lastPurchaseDate",
            operator: "contains",
            value: productSearchQuery,
          },
          {
            field: "lastApplicationDate",
            operator: "contains",
            value: productSearchQuery,
          },
        ],
      };
      setFilter(filterObj);
    } else {
      setFilter(initialFilters);
    }
  };

  // Update filter when search query changes
  useEffect(() => {
    updateFilter();
  }, [productSearchQuery]);

  const handleDetailModalClose = () => {
    setDetailModalOpen(false);
    setSelectedItem(null);
  };

  const handleEditModalClose = async () => {
    setEditModalOpen(false);
    setSelectedItem(null);
  };

  const excelExport = () => {
    if (exportRef.current !== null) {
      const processedData: DataResult = process(transformedData, {
        filter: filter,
      });
      exportRef.current.save(processedData);
    }
  };

  // Show loading spinner while data is being fetched
  if (isLoading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="400px"
        flexDirection="column"
        gap={2}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="textSecondary">
          Loading inventory data...
        </Typography>
      </Box>
    );
  }

  return (
    <Box display={"flex"} p={3} gap={2} flexDirection={"column"}>
      <Box display={"flex"} gap={2}>
        <TextField
          placeholder="Search Product"
          variant="outlined"
          size="small"
          value={productSearchQuery}
          onChange={handleProductSearchChange}
          onKeyPress={handleProductSearchKeyPress}
          InputProps={{
            endAdornment: (
              <IconButton
                onClick={handleClearSearch}
                style={productSearchQuery ? {} : { visibility: "hidden" }}
              >
                <Clear />
              </IconButton>
            ),
            startAdornment: <Search />,
          }}
        />
        <Button
          variant="contained"
          startIcon={<Launch />}
          onClick={excelExport}
          disabled={isLoading} // Disable export while loading
        >
          Export
        </Button>
      </Box>
      <Box>
        <DataGridWithSort
          ref={exportRef}
          data={transformedData}
          dataItemKey="productId"
          filter={filter}
          columns={gridColumns}
        />
      </Box>
      
      {/* View Modal - Read Only */}
      {detailModalOpen && selectedItem && (
        <InventoryDetailModal
          open={detailModalOpen}
          handleClose={handleDetailModalClose}
          productId={selectedItem.productId}
          productName={selectedItem.productName}
          unit = {selectedItem.unit}
        />
      )}

      {/* Edit Modal - Editable Purchases */}
      {editModalOpen && selectedItem && (
        <EditInventoryModal
          open={editModalOpen}
          handleClose={handleEditModalClose}
          productId={selectedItem.productId}
          productName={selectedItem.productName}
        />
      )}
    </Box>
  );
};